import { useEffect } from "react";

/**
 * Enables click-and-drag horizontal scrolling (for mouse users) on any
 * horizontally-scrollable container — e.g. the `overflow-x-auto` card carousels
 * on home / marketplace / dashboards.
 *
 * Why: those carousels rely on native horizontal scroll. A trackpad (Mac) can
 * scroll them with a two-finger swipe, and touch devices drag natively — but a
 * desktop MOUSE has no way to scroll them (the scrollbar is hidden and mouse
 * wheels only scroll vertically), so users on Windows etc. can't slide them.
 *
 * This mounts once, listens at the document level, and on mouse-drag scrolls the
 * nearest horizontally-scrollable ancestor. Slick/Swiper carousels are unaffected
 * (their tracks use overflow:hidden, so they don't match). Touch devices are
 * skipped since they already work.
 */
export default function DragToScroll() {
  useEffect(() => {
    // Only enhance fine pointers (mouse); touch/trackpad swipe already works.
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
      return;
    }

    let target = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;
    let moved = 0;

    const isScrollableX = (el) => {
      if (!(el instanceof HTMLElement)) return false;
      const style = getComputedStyle(el);
      const ox = style.overflowX;
      return (ox === "auto" || ox === "scroll") && el.scrollWidth > el.clientWidth + 2;
    };

    const findScrollable = (el) => {
      while (el && el !== document.body && el !== document.documentElement) {
        if (isScrollableX(el)) return el;
        el = el.parentElement;
      }
      return null;
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      // ignore drags that start on form controls
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const el = findScrollable(e.target);
      if (!el) return;
      target = el;
      dragging = true;
      moved = 0;
      startX = e.pageX;
      startScrollLeft = el.scrollLeft;
      el.style.cursor = "grabbing";
      el.style.userSelect = "none";
    };

    const onMouseMove = (e) => {
      if (!dragging || !target) return;
      const dx = e.pageX - startX;
      if (Math.abs(dx) > moved) moved = Math.abs(dx);
      target.scrollLeft = startScrollLeft - dx;
      if (moved > 5) e.preventDefault();
    };

    const onMouseUp = () => {
      if (!dragging) return;
      const el = target;
      dragging = false;
      if (el) {
        el.style.cursor = "";
        el.style.userSelect = "";
      }
      target = null;
      // If we actually dragged, swallow the click so dragging a card doesn't
      // also trigger its navigation/onClick.
      if (moved > 5) {
        const swallow = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        document.addEventListener("click", swallow, { capture: true, once: true });
        // safety: remove if no click fires
        setTimeout(() => {
          document.removeEventListener("click", swallow, { capture: true });
        }, 50);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("mouseleave", onMouseUp);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("mouseleave", onMouseUp);
    };
  }, []);

  return null;
}
